/* eslint-disable consistent-return */
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import './AgentLedger.scss';
import sub from 'date-fns/sub';
import add from 'date-fns/add';
import AppTextInput from '../../../shared/components/app-text-input/AppTextInput';
import AppButton from '../../../shared/components/app-button/AppButton';
import {
  API_DATE_FORMAT,
  TODAY_DATE,
} from '../../../shared/constants/Constants';
import { getLedgerApi, getUserDetails } from '../../../api/profileApi';
import AppLoading from '../../../shared/components/app-loading/AppLoading';
import AppAlert from '../../../shared/components/app-alert/AppAlert';
import LedgerTable from './LedgerTable';
import {
  formatDate,
  getNewDate,
  isAfterDate,
} from '../../../shared/helper-utils/appUtils';
import {
  getUserAgentId,
  getStringFromLS,
} from '../../../shared/helper-utils/localStorage';
import AccountsPageHorizontalBar from '../accounts/AccountsPageHorizontalBar';
import { useAppContext } from '../../../saas/AppConfigWrapper';
import { APP_USER_AUTH } from '../../../saas/AppStorageStaticNames';

export default function AgentLedger() {
  const intl = useIntl();
  const { appConfig } = useAppContext();
  const formattedMessage = (message) => {
    return intl.formatMessage({ id: message });
  };
  const apiProcessingRef = useRef(false);
  const userRef = useRef(undefined);
  const [ledger, setLedger] = useState(undefined);
  // const [validationError, setValidationError] = useState(false);
  const [dates, setDates] = useState(undefined);
  const sortTypeRef = useRef('asc');
  // const [emailId, setEmailId] = useState("");
  const [fromDate, setFromDate] = useState(
    formatDate(sub(getNewDate(TODAY_DATE), { days: 3 }), API_DATE_FORMAT),
  );
  const [toDate, setToDate] = useState(
    formatDate(add(getNewDate(TODAY_DATE), { days: 1 }), API_DATE_FORMAT),
  );
  const [dateValidationError, setDateValidationError] = useState({
    fromDate: false,
    toDate: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(undefined);

  const isValidDateValue = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const validateLedgerDates = () => {
    const nextDateValidationError = {
      fromDate: !isValidDateValue(fromDate),
      toDate: !isValidDateValue(toDate),
    };

    setDateValidationError(nextDateValidationError);

    if (nextDateValidationError.fromDate || nextDateValidationError.toDate) {
      setAlert({
        variant: 'error',
        title: 'Agent Ledger',
        description: 'Please select correct date',
      });
      return false;
    }

    return true;
  };

  const getUser = async () => {
    if (!apiProcessingRef.current) {
      setIsLoading(true);
      apiProcessingRef.current = true;
      const agentId = getUserAgentId();
      const response = await getUserDetails(agentId);
      if (response && response.ApiStatus) {
        userRef.current = response.ApiStatus.Result.User;
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUser();
  }, []);

  const setLedgerData = (result) => {
    const rows = Array.isArray(result.Ledgers.LedgerRow)
      ? result.Ledgers.LedgerRow
      : [result.Ledgers.LedgerRow];
    setDates({
      fromDate,
      toDate,
    });
    setLedger(rows);
  };
  const handleGetLedger = async () => {
    if (!validateLedgerDates()) {
      return;
    }

    if (isAfterDate(getNewDate(fromDate), getNewDate(toDate))) {
      setAlert({
        variant: 'error',
        title: 'Agent Ledger',
        description: 'From Date should not be greater than To Date',
      });
      return;
    }
    setIsLoading(true);
    const agentId = getUserAgentId();
    const res = await getUserDetails(agentId);
    if (res && res.ApiStatus) {
      userRef.current = res.ApiStatus.Result.User;
    }
    const response = await getLedgerApi(fromDate, toDate);
    if (response) {
      if (response.ApiStatus) {
        if (response.ApiStatus.Status === 'OK') {
          setAlert(undefined);
          const { Result } = response.ApiStatus;
          setLedgerData(Result);
        } else {
          setAlert({
            variant: 'error',
            title: formattedMessage('show_ledger'),
            description: response.ApiStatus.Message,
          });
        }
      } else if (response.Root) {
        if (response.Root.Status === 'OK') {
          setAlert(false);
          const { Result } = response.Root;
          setLedgerData(Result);
        } else {
          setAlert({
            variant: 'error',
            title: formattedMessage('show_ledger'),
            description: response.Root.Message,
          });
        }
      }
    } else {
      alert({
        variant: 'error',
        title: formattedMessage('show_ledger'),
        description: formattedMessage('failed_to_get_ledger_alert'),
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleGetLedger();
  }, []);

  const hanndleExcelDownload = async () => {
    if (!validateLedgerDates()) {
      return;
    }

    if (isAfterDate(getNewDate(fromDate), getNewDate(toDate))) {
      setAlert({
        variant: 'error',
        title: 'Agent Ledger',
        description: 'From Date should not be greater than To Date',
      });
      return;
    }
    const agentId = getUserAgentId();
    const userAuth = getStringFromLS(APP_USER_AUTH);

    const link = `${appConfig?.appMainUrl}v2dispatch.jsp?sessiontoken=${userAuth}&opid=RP036&actioncode=GETLEDGER&agentid=${agentId}&fromdate=${fromDate}&todate=${toDate}&resultformat=excel&responseformat=excel`;
    const element = document.createElement('a');
    element.href = link;
    element.target = '_blank';
    element.download = `ledger_${fromDate}_${toDate}.xls`;
    element.click();
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setDateValidationError((prevState) => ({
      ...prevState,
      [name]: value !== '' && !isValidDateValue(value),
    }));

    if (name === 'fromDate') {
      setFromDate(value);
    } else {
      setToDate(value);
    }
  };

  const handleDateKeyDown = (event) => {
    if (event.key !== 'Tab') {
      event.preventDefault();
    }
  };

  const handleDatePickerOpen = (event) => {
    if (event.currentTarget.showPicker) {
      event.currentTarget.showPicker();
    }
  };

  const handleSort = () => {
    const list = [...ledger];
    if (sortTypeRef.current === 'asc') {
      sortTypeRef.current = 'desc';
    } else {
      sortTypeRef.current = 'asc';
    }
    if (sortTypeRef.current === 'asc') {
      list.sort((a, b) =>
        getNewDate(a.TxDate) < getNewDate(b.TxDate) ? -1 : 1,
      );
    } else {
      list.sort((a, b) =>
        getNewDate(a.TxDate) > getNewDate(b.TxDate) ? -1 : 1,
      );
    }
    setLedger(list);
  };

  return (
    <>
      <AccountsPageHorizontalBar selectedPage="agent-ledger" />
      <section className="legder-page ">
        <AppLoading isLoading={isLoading} />
        {alert && (
          <AppAlert
            variant="error"
            showIcon
            onHide={() => setAlert(undefined)}
            title={alert.title}
            description={alert.description}
          />
        )}
        <section className="ledger-page_container">
          <div className="manage-all-bookings_heading">
            {formattedMessage('agent_ledger')}
          </div>
          <div className="separator"> </div>
          <section className="body p-3">
            <div className="input-bar">
              <div className="flex-bw">
                <div className="flex-1 me-3">
                  <AppTextInput
                    label={formattedMessage('from_date')}
                    type="date"
                    name="fromDate"
                    errorText={
                      dateValidationError.fromDate &&
                      'Please select correct date'
                    }
                    id="fromDate"
                    placeholderText=""
                    value={fromDate}
                    onChange={handleDateChange}
                    onKeyDown={handleDateKeyDown}
                    onPaste={(event) => event.preventDefault()}
                    onClick={handleDatePickerOpen}
                  />
                </div>
                <div className="flex-1 me-3">
                  <AppTextInput
                    label={formattedMessage('to_date')}
                    type="date"
                    name="toDate"
                    errorText={
                      dateValidationError.toDate && 'Please select correct date'
                    }
                    id="toDate"
                    placeholderText=""
                    value={toDate}
                    onChange={handleDateChange}
                    onKeyDown={handleDateKeyDown}
                    onPaste={(event) => event.preventDefault()}
                    onClick={handleDatePickerOpen}
                  />
                </div>
                <div>
                  <AppButton
                    className="me-3"
                    btnText={formattedMessage('get_ledger_btn')}
                    onClick={handleGetLedger}
                  />
                  <AppButton
                    variant="secondary"
                    btnText={formattedMessage('excel_ledger_btn')}
                    onClick={hanndleExcelDownload}
                  />
                </div>
              </div>
            </div>
          </section>
          <div className="separator" />
        </section>
        {userRef.current && (
          <div className="detail-list p-3 mt-2 flex-column flex-sm-row">
            <div className="d-flex flex-column">
              <span className="head">{formattedMessage('agent')}</span>
              <span className="head-value">{userRef.current.AgentId}</span>
            </div>
            <div className="d-flex flex-column">
              <span className="head">{formattedMessage('email')}</span>
              <span className="head-value">{userRef.current.ContactEmail}</span>
            </div>
            <div className="d-flex flex-column">
              <span className="head">{formattedMessage('address')}</span>
              <span className="head-value">{`${userRef.current.Address1}, ${userRef.current.Address2} ${userRef.current.City}, ${userRef.current.PinCode}`}</span>
            </div>
            <div className="d-flex flex-column">
              <span className="head">{formattedMessage('phone')}</span>
              <span className="head-value">
                {userRef.current.ContactNumber}
              </span>
            </div>
            <div className="d-flex flex-column">
              <span className="head">{formattedMessage('mobile')}</span>
              <span className="head-value">{userRef.current.MobileNumber}</span>
            </div>
          </div>
        )}
        {ledger && (
          <div className="app-container mt-sm-4 mt-0">
            <LedgerTable
              fromDate={dates.fromDate}
              toDate={dates.toDate}
              ledgerRows={ledger}
              sortType={sortTypeRef.current}
              onSort={handleSort}
            />
          </div>
        )}
      </section>
    </>
  );
}
